import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Drawer, Box, Typography, Stack, TextField, Button,
    Divider, IconButton, Chip, FormControlLabel, Switch, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToPhotosRoundedIcon from '@mui/icons-material/AddToPhotosRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ApiClient from '../../../common/API';
import { useQueryClient } from 'react-query';
import { useGroupList, useUserList } from '../../../common/API/hooks';
import { getSelectedOption } from '../../../common/utils';
import DropdownCheckboxV3 from '../../../common/components/DropdownCheckboxV3';
import ConfirmationModal from '../../../common/components/ConfirmationModal';
import handleError from '../ErrorHandling';

const BRAND = '#0F2FEB';
const EMPTY_MODULE = { name: '', allowed_users: [], allowed_groups: [], is_public: false };

export default function ModuleDrawer({ isOpen, mode, module, onClose }) {
    const queryClient = useQueryClient();
    const { data: userList, isLoading: userLoading } = useUserList();
    const { data: groupList, isLoading: groupLoading } = useGroupList();

    const [form, setForm] = useState(EMPTY_MODULE);
    const [hasChanges, setHasChanges] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [saving, setSaving] = useState(false);

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (isOpen) {
            if (isEdit && module) {
                setForm({
                    name: module.name || '',
                    allowed_users: module.allowed_users || [],
                    allowed_groups: module.allowed_groups || [],
                    is_public: module.is_public ?? false,
                });
            } else {
                setForm(EMPTY_MODULE);
            }
            setHasChanges(false);
        }
    }, [isOpen, module, isEdit]);

    const userOptions = React.useMemo(() => {
        if (userLoading || !userList) return [];
        return userList.map(u => ({
            value: u.username,
            label: `${u.first_name} ${u.last_name} (${u.username})`,
            key: u.id,
        }));
    }, [userList, userLoading]);

    const groupOptions = React.useMemo(() => {
        if (groupLoading || !groupList) return [];
        return groupList.map(g => ({ value: g.name, label: g.name, key: g.id }));
    }, [groupList, groupLoading]);

    const handleNameChange = (e) => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, name: e.target.value }));
    };

    const handleUserChange = (_, newValue) => {
        setHasChanges(true);
        // DropdownCheckboxV3 emits an array of selected values.
        setForm(prev => ({ ...prev, allowed_users: newValue || [] }));
    };

    const handleGroupChange = (_, newValue) => {
        setHasChanges(true);
        // DropdownCheckboxV3 emits an array of selected values.
        setForm(prev => ({ ...prev, allowed_groups: newValue || [] }));
    };

    const handlePublicChange = () => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, is_public: !prev.is_public }));
    };

    const userTagsRenderer = React.useCallback((value, getTagProps) => {
        if (!value?.length) return [];
        return value.map((tag, index) => (
            <Chip
                {...getTagProps({ index })}
                key={index}
                size="small"
                label={tag?.value}
                sx={{ fontSize: '0.7rem' }}
            />
        ));
    }, []);

    const handleClose = () => {
        if (hasChanges) setShowWarning(true);
        else onClose();
    };

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            allowed_users: form.is_public ? [] : form.allowed_users,
            allowed_groups: form.is_public ? [] : form.allowed_groups,
            is_public: form.is_public,
        };
        try {
            if (isEdit) {
                await ApiClient().patch(`module/${module.id}/`, payload);
            } else {
                await ApiClient().post('module/', payload);
            }
            // Await invalidation so the table and stats are fresh before the drawer closes
            await queryClient.invalidateQueries(['module']);
            toast.success(isEdit ? 'Module updated successfully!' : 'Module created successfully!');
            onClose();
        } catch (error) {
            toast.error(handleError(error, 'Module', isEdit ? 'edit' : 'save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 540 }, display: 'flex', flexDirection: 'column' },
                }}
            >
                {/* Header */}
                <Box sx={{ px: 3, py: 2.5, bgcolor: BRAND, color: 'white', flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            {isEdit
                                ? <EditNoteRoundedIcon sx={{ fontSize: '1.6rem' }} />
                                : <AddToPhotosRoundedIcon sx={{ fontSize: '1.6rem' }} />}
                            <Typography variant="h6" fontWeight="bold">
                                {isEdit ? 'Edit Module' : 'Register New Module'}
                            </Typography>
                        </Stack>
                        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    {isEdit && module && (
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, ml: 4 }}>
                            {module.name}
                            {' '}
                            &bull;
                            {' '}
                            {module.is_public ? 'Public' : `${module.allowed_users?.length || 0} user${module.allowed_users?.length !== 1 ? 's' : ''}, ${module.allowed_groups?.length || 0} group${module.allowed_groups?.length !== 1 ? 's' : ''}`}
                        </Typography>
                    )}
                </Box>

                <Divider />

                {/* Scrollable Form Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                    <Stack spacing={3}>
                        <TextField
                            label="Module Name / Path"
                            size="small"
                            fullWidth
                            required
                            autoFocus
                            placeholder="e.g. retail/sales-dashboard"
                            value={form.name}
                            onChange={handleNameChange}
                            helperText="Use the URL path of the module (without leading/trailing slashes)."
                        />

                        {/* Public toggle */}
                        <Box sx={{
                            p: 2, borderRadius: 2,
                            bgcolor: form.is_public ? '#EDF7EE' : '#F8F9FA',
                            border: '1px solid', borderColor: form.is_public ? '#81C784' : '#E0E0E0',
                        }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {form.is_public
                                        ? <PublicRoundedIcon sx={{ color: '#2E7D32', fontSize: '1.2rem' }} />
                                        : <LockRoundedIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />}
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {form.is_public ? 'Public Module' : 'Restricted Module'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {form.is_public
                                                ? 'Visible to all users'
                                                : 'Visible only to selected users and groups'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.is_public}
                                            onChange={handlePublicChange}
                                            color="success"
                                            size="small"
                                        />
                                    }
                                    label=""
                                    sx={{ mr: 0 }}
                                />
                            </Stack>
                        </Box>

                        {form.is_public && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                User and group restrictions are ignored for public modules.
                            </Alert>
                        )}

                        {!form.is_public && (
                            <>
                                <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                        Allowed Users
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Users who can access this module directly (not via group membership).
                                    </Typography>
                                    <DropdownCheckboxV3
                                        options={userOptions}
                                        label="Select User(s)"
                                        placeholder="Select User(s)"
                                        limitTags={3}
                                        loading={userLoading}
                                        onChange={handleUserChange}
                                        value={getSelectedOption(form.allowed_users, userOptions) || []}
                                        tagsRenderer={userTagsRenderer}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                        Allowed Groups
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        All members of selected groups will have access to this module.
                                    </Typography>
                                    <DropdownCheckboxV3
                                        options={groupOptions}
                                        label="Select Group(s)"
                                        placeholder="Select Group(s)"
                                        limitTags={3}
                                        loading={groupLoading}
                                        disabled={groupLoading}
                                        onChange={handleGroupChange}
                                        value={getSelectedOption(form.allowed_groups, groupOptions) || []}
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </Box>

                <Divider />

                {/* Footer */}
                <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ textTransform: 'none', color: BRAND, borderColor: BRAND, minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!form.name.trim() || saving}
                            sx={{
                                textTransform: 'none', bgcolor: BRAND, minWidth: 130, fontWeight: 600,
                                '&:hover': { bgcolor: '#0A22C0' },
                            }}
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Register Module'}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            {showWarning && (
                <ConfirmationModal
                    isOpen={showWarning}
                    onClose={() => setShowWarning(false)}
                    onConfirm={() => { setShowWarning(false); onClose(); }}
                    icon={<WarningAmberIcon sx={{ fontSize: '60px' }} color="warning" />}
                    title="Discard Changes?"
                    message="You have unsaved changes. Are you sure you want to close without saving?"
                />
            )}
        </>
    );
}

